import GiftLoadError from '@activities/gift/_shared/components/GiftLoadError';
import useGiftItemNutrition from '@activities/gift/GiftItemActivity/hooks/useGiftItemNutrition';
import Column from '@components/Column';
import Header from '@components/Header';
import Row from '@components/Row';
import Skeleton from '@components/Skeleton';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { useTranslation } from 'react-i18next';

interface Params {
    giftItemId: string;
}

declare module '@stackflow/config' {
    interface Register {
        GiftMenuDetailActivity: Params;
    }
}

const NUTRITION_ROWS = [
    'calories',
    'carbohydrate',
    'sugar',
    'sodium',
    'protein',
    'fat',
    'servingSize',
    'saturatedFat',
    'caffeine',
    'cholesterol',
    'transFat',
    'origin',
] as const;

const GiftMenuDetailActivity: ActivityComponentType<'GiftMenuDetailActivity'> = ({ params }) => {
    const { t } = useTranslation('gift');
    const { t: tItem } = useTranslation('item');
    const {
        data: nutrition, isPending, isError, isFetching, refetch,
    } = useGiftItemNutrition(params.giftItemId);

    const rows = NUTRITION_ROWS
        .map((key) => {
            const value = nutrition?.[key];
            if (value == null || value === '') return null;
            return { key, value: String(value) };
        })
        .filter((row): row is { key: typeof NUTRITION_ROWS[number]; value: string } => !!row);

    const hasAllergy = !!nutrition?.allergyComment?.trim();

    return (
        <AppScreen>
            <Header title='영양정보' />
            <Container>
                {isPending && (
                    <Column fullWidth spacing={8} aria-busy>
                        <Column fullWidth spacing={2}>
                            <Skeleton width={96} height={22} />
                            <Skeleton width='70%' height={20} />
                        </Column>
                        <Column fullWidth spacing={2}>
                            <Skeleton width={120} height={22} />
                            <Rows>
                                {Array.from({ length: 6 }, (_, index) => (
                                    <InfoRow key={index} fullWidth justify='space-between'>
                                        <Skeleton width={64} height={20} />
                                        <Skeleton width={48} height={20} />
                                    </InfoRow>
                                ))}
                            </Rows>
                        </Column>
                    </Column>
                )}

                {isError && <GiftLoadError message={t('giftItem.loadError')} isFetching={isFetching}
                    onRetry={() => refetch()} />}
                {!isPending && !isError && (
                    <Column fullWidth spacing={8}>
                        <Column fullWidth spacing={2}>
                            <Text size='title' fontWeight='bold'>
                                {t('giftItem.menuDetail.allergyTitle')}
                            </Text>
                            <Text size='body-short' color={colors.textSecondary}>
                                {hasAllergy ? nutrition?.allergyComment : t('giftItem.menuDetail.noData')}
                            </Text>
                        </Column>
                        <Column fullWidth spacing={2}>
                            <Text size='title' fontWeight='bold'>
                                {t('giftItem.menuDetail.nutritionTitle')}
                            </Text>
                            {rows.length > 0 ? (
                                <Rows>
                                    {rows.map((row) => (
                                        <InfoRow key={row.key} fullWidth justify='space-between'>
                                            <Text color={colors.textSecondary} fontWeight='medium'>
                                                {tItem(`nutrition.${row.key}`)}
                                            </Text>
                                            <Value>{row.value}</Value>
                                        </InfoRow>
                                    ))}
                                </Rows>
                            ) : (
                                <Text size='body-short' color={colors.textSecondary}>
                                    {t('giftItem.menuDetail.noData')}
                                </Text>
                            )}
                        </Column>
                    </Column>
                )}
            </Container>
        </AppScreen>
    );
};

const Container = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: var(--spacing-6) var(--spacing-5);
    background: ${colors.white};
`;

const Rows = styled.div`
    width: 100%;
`;

const InfoRow = styled(Row)`
    padding: 14px 0;
    border-bottom: 1px solid ${colors.gray2};

    &:last-of-type {
        border-bottom: none;
    }
`;

const Value = styled(Text)`
    text-align: right;
    white-space: normal;
    word-break: keep-all;
`;

export default GiftMenuDetailActivity;
